// Angular + External
import {
  Component,
  OnInit,
  ViewEncapsulation,
  Input,
  HostBinding,
  OnDestroy,
  ElementRef,
  HostListener,
  ViewChild,
  AfterViewInit,
} from "@angular/core";
import { trigger, state, animate, transition, style, AUTO_STYLE } from "@angular/animations";
import { Router } from "@angular/router";
// BFX
// Models
import { User } from "../bfx-common/models/user.model";
// Services
import { BfxAuthService } from "../bfx-common/services/bfx-auth.service";
import { BfxConfigService } from "../../shared/auth-services/system-config/bfx-config.service";
import { TranslateService } from "@ngx-translate/core";
import {
  ConnectionStatusPromiseService,
  ConnectionStatusState,
  ConnectionStatus,
} from "shared/broker-client-services";
import { BfxUIConfiguration } from "../../shared/auth-services/system-config/model/config.models";
import { firstValueFrom, Subscription } from "rxjs";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";

@Component({
  selector: "bfx-login",
  templateUrl: "./html/login.component.html",
  animations: [
    trigger("errorMessageAnimation", [
      state("void", style({ height: "0px" })),
      state("active", style({ height: AUTO_STYLE })),
      transition(":enter", animate("200ms 100ms ease-in")),
      transition(":leave", [animate("100ms ease-out")]),
    ]),
    trigger("loadingAnimation", [
      state("active", style({ opacity: 1 })),
      state("void", style({ opacity: 0 })),
      transition(":enter", animate("400ms 100ms ease-in")),
      transition(":leave", animate("100ms ease-out")),
    ]),
  ],
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {
  @HostBinding("class.ready-visibility-hook")
  public readonly readyVisibilityHook: boolean = true;
  private hadDisconnect: boolean = false;
  private hadConnection: boolean = false;
  public loginErrors = [];

  // no reconnect logic yet

  public connectionStatus: number = 0;
  public connectionStatusText: string = "Initializing";
  public capsLockStatusText: string = "";
  public isCapsLockOn: boolean = false;

  private readonly RES_VALUE = "auth.cred.reset";
  private readonly EXP_VALUE = "auth.credential.expired";

  public get frameTag() {
    return document.body.dataset.frameTag;
  }

  public control: any = {
    debug: true,
    loadingDelay: 3000,
    authLoading: false,
    authError: false,
    authErrorTitle: "Error",
    authErrorMsg: "Unknown error occurred",
    authSignIn: "",
    disclaimerTxt: "",
    showDisclaimer: false,
    lockInput: false,
    lockSignIn: true,
    downloadLink: "",
    showDownloadLink: false,
  };

  private uiConfigSubscription: Subscription;
  private installConfigSubscription: Subscription;
  private serverStatusSubscription: Subscription;

  config: BfxUIConfiguration = null;

  public user: User = new User("", "");

  private _ngViewCheckCount: number = 0;

  public openFin: boolean = false;

  public downloadBundle: string = null;

  @Input() accessRestricted: string;

  @ViewChild("userName", { static: true })
  public userNameElement: ElementRef;

  constructor(
    private _router: Router,
    private _authService: BfxAuthService,
    private _configService: BfxConfigService,
    private _translationService: TranslateService,
    private _connectionStatusService: ConnectionStatusPromiseService,
    private _element: ElementRef,
  ) {
    NativeDesktopSupport.start(this._element.nativeElement);
  }

  ngOnInit(): void {
    this.checkOpenFin();
    this.subscribeToUIConfiguration();
    this.subscribeToInstallConfiguration();
    this.subscribeToServerStatus();
    this.enableInput();

    this._translationService.use("en");

    firstValueFrom(this._translationService.get("auth.sign.in")).then((value) => {
      this.control.authSignIn = value;
    });
  }

  ngOnDestroy() {
    this.uiConfigSubscription.unsubscribe();
    this.installConfigSubscription.unsubscribe();
    this.serverStatusSubscription.unsubscribe();
  }

  ngAfterViewInit(): void {
    setTimeout(
      (() => {
        this.userNameElement.nativeElement.focus();
        this.userNameElement.nativeElement.select();
      }).bind(this),
      100,
    );
  }

  /**
   * Check for openfin and display unsupported message
   */
  private checkOpenFin(): void {
    this.openFin = NativeDesktopSupport.isOpenFin();
    if (this.openFin) {
      let sso = window.document.body.hasAttribute("lid");
      this.downloadBundle = sso ? "omsinstallsso.zip" : "omsinstall.zip";
    }
  }

  /***********************************/
  /* Subscriptions */

  private subscribeToUIConfiguration(): void {
    this.uiConfigSubscription = this._configService.uiConfig$.subscribe(
      (config) => {
        if (config != null) {
          this.control.debug = config.general.debug;
        }
      },
      (err) => {
        logger.out.error("ERROR: LoginComponent.subscribeToUIConfiguration(): ", err);
      },
    );
  }

  private subscribeToInstallConfiguration(): void {
    this.installConfigSubscription = this._configService.installConfig$.subscribe(
      (config) => {
        if (config != null) {
          this.control.disclaimerTxt = config.disclaimer;
          this.control.showDisclaimer =
            this.control.disclaimerTxt && this.control.disclaimerTxt.length > 0;
          this.control.downloadLink = config.download_text;
          this.control.showDownloadLink =
            !NativeDesktopSupport.isNativeDesktopContainer() &&
            this.control.downloadLink &&
            this.control.downloadLink.length > 0;
        }
      },
      (err) => {
        logger.out.error("ERROR: LoginComponent.subscribeToInstallConfiguration(): ", err);
      },
    );
  }

  private subscribeToServerStatus(): void {
    this.serverStatusSubscription = this._connectionStatusService
      .getConnectionEvents()
      .subscribe((event: ConnectionStatus) => {
        this.connectionStatus = event.getStatus();
        switch (this.connectionStatus) {
          case ConnectionStatusState.NONE:
            this.connectionStatusText = "Initializing"; // none
            this.disableSignIn();
            break;

          case ConnectionStatusState.CONNECTED:
          case ConnectionStatusState.RELOAD: // connection re-established, login permitted
            this.connectionStatusText = "Online";
            this.enableSignIn();
            break;
          case ConnectionStatusState.ERROR:
            this.connectionStatusText = "Error";
            this.disableSignIn();
            break;
          case ConnectionStatusState.DISCONNECTED:
          case ConnectionStatusState.RECONNECTING:
            this.connectionStatusText = "Reconnecting";
            this.disableSignIn();
            break;
        }
      });
  }

  private enableInput(): void {
    this.control.lockInput = false;
  }

  private disableInput(): void {
    this.control.lockInput = true;
  }

  private enableSignIn(): void {
    this.control.lockSignIn = false;
  }

  private disableSignIn(): void {
    this.control.lockSignIn = true;
  }

  /***********************************/
  /* Actions */
  public enterLogin(): void {
    if (!this.control.authLoading && !this.control.lockSignIn) {
      this.login();
    }
  }

  public login(): void {
    if (!this.validateUserName() || !this.validatePassword()) return;
    this.control.authLoading = true;
    this.disableInput();
    this._authService.login(this.user).then(
      (resolve) => {
        if (resolve.isSuccess) {
          // on success
          this.control.authLoading = false;
          this._router.navigate(["main"]);
        } else {
          this.control.authError = true;
          this.control.authErrorTitle = this._translationService.instant("auth.error.title");
          if (resolve.errors) {
            let errMsgId = JSON.parse(resolve.errors.errorMessages[0].toString())["id"];
            if (errMsgId === this.RES_VALUE || errMsgId === this.EXP_VALUE) {
              this._router.navigate(["change-password"]);
            } else
              this.control.authErrorMsg = this.handleErrorMessages(resolve.errors.errorMessages);
          }
          this.control.authLoading = false;
          this.enableInput();
        }
      },
      (reject) => {
        this.control.authLoading = true;
        this.control.authError = true;
        this.enableInput();
      },
    );
  }

  /***********************************/
  /* Helpers */

  private handleErrorMessages(errors: any[]): string {
    let message = "";
    errors
      .map((e) => JSON.parse(e))
      .forEach((e) => {
        if (e["id"] && e["id"].length > 0)
          message += this._translationService.instant(e["id"]) + "\n";
      }) + "  ";
    return message;
  }

  public validateUserName(): boolean {
    return this.user.username.length > 0;
  }

  public validatePassword(): boolean {
    return this.user.password.length > 0;
  }

  @HostListener("window:click", ["$event"]) onClick(event) {
    this.setCapsLockStatusText(event);
  }

  @HostListener("window:keydown", ["$event"])
  onKeyDown(event) {
    this.setCapsLockStatusText(event);
  }

  @HostListener("window:keyup", ["$event"])
  onKeyUp(event) {
    this.setCapsLockStatusText(event);
  }

  private setCapsLockStatusText(event) {
    this.isCapsLockOn = event.getModifierState && event.getModifierState("CapsLock");
    this.capsLockStatusText = this.isCapsLockOn
      ? this._translationService.instant("auth.input.caps-lock-on")
      : "";
  }
}
